import React from "react";
import { saveAs } from "file-saver";
import moment from "moment";
import "moment/locale/fr";

import {
  clientService,
  complianceService,
  incidentService,
  noteService,
  projectService,
  tailgateService,
  userService,
  submissionService,
  documentService,
  selectService,
} from "services";
import { history, dateFormat, previewDocument, selectProps } from "utils";
import { Select, Tag } from "antd";

import {
  COMPLIANCE_STATUS_ACCEPT,
  COMPLIANCE_STATUS_ACCEPTCHANGE,
  COMPLIANCE_STATUS_DENY,
  COMPLIANCE_STATUS_LIST,
  INTERVENTION_TYPES,
  interventionType,
} from "constants/dbValues";

import SelectsContext from "constants/selectsContext";
import OrderedLookupFilter from "components/_parts/OrderedLookupFilter";

const ellipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: "200px",
  maxWidth: "400px",
};

export const tables = {
  clients: {
    title: "CLB - Clients",
    service: clientService,
    columns: [
      {
        field: "name",
        title: "Nom (name)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "city",
        title: "Ville (city)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "tel",
        title: "# Tél. (tel)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "170px" },
      },
      {
        field: "contact_name",
        title: "Nom du contact (contact_name)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
    ],
    onRowClick: (e, data) => history.push(`/clients/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un client",
        isFreeAction: true,
        onClick: (event) => history.push("/clients/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer le client",
        onClick: (event, rowData) => history.push("/clients/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },
  incidents: {
    title: "CLB - Incidents",
    service: incidentService,
    dataFormat: (data) => {
      return data.map((d) => {
        d.project_active = d.project.disabled === 0 || d.project.disabled === false ? 1 : 0;
        return d;
      });
    },
    columns: [
      {
        field: "project_active",
        title: "Actif (project_active)",
        type: "boolean",
        orderField: "projects.disabled",
        hidden: false,
      },
      {
        field: "accident",
        title: "Type (accident)",
        render: (rowData) => {
          if (rowData.accident == 1) return "Accident";
          if (rowData.accident == 2) return "Near Miss";
          return "Incident";
        },
        type: "string",
        lookup: { 1: "Accident", 0: "Incident", 2: "Near Miss" },
        hidden: false,
        // cellStyle: { minWidth: '160px' },
      },
      {
        field: "project_code",
        title: "Code du projet (project_code)",
        render: (rowData) => (rowData.project ? rowData.project.code : ""),
        orderField: "projects.code",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "project_label",
        title: "Projet (project_label)",
        render: (rowData) => (rowData.project ? rowData.project.label : ""),
        orderField: "projects.label",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "date",
        title: "Date (date)",
        render: (rowData) => (rowData.date ? moment(rowData.date).format(dateFormat) : ""),
        type: "date",
        filtering: false,
        hidden: false,
        cellStyle: { minWidth: "130px" },
        defaultSort: "desc",
      },
      {
        field: "details",
        title: "Détails (details)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
    ],
    onRowClick: (e, data) => history.push(`/incidents/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un incident",
        isFreeAction: true,
        onClick: (event) => history.push("/incidents/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer l'incident",
        onClick: (event, rowData) => history.push("/incidents/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },
  compliances: {
    title: "CLB - Non-conformités",
    service: complianceService,
    fetcher: "list",
    columns: [
      {
        field: "id",
        title: "Numéro (id)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "130px" },
      },
      {
        field: "type_id",
        title: "Type (type_id)",
        render: (rowData) => (rowData.compliance_type ? rowData.compliance_type.label : ""),
        orderField: "compliance_types.label",
        type: "string",
        lookup: {},
        lookupKey: "ComplianceType",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "status",
        title: "Statut (status)",
        type: "string",
        lookup: {
          [COMPLIANCE_STATUS_ACCEPT]: COMPLIANCE_STATUS_LIST.find(
            (c) => c.id === COMPLIANCE_STATUS_ACCEPT,
          ).label,
          [COMPLIANCE_STATUS_ACCEPTCHANGE]: COMPLIANCE_STATUS_LIST.find(
            (c) => c.id === COMPLIANCE_STATUS_ACCEPTCHANGE,
          ).label,
          [COMPLIANCE_STATUS_DENY]: COMPLIANCE_STATUS_LIST.find(
            (c) => c.id === COMPLIANCE_STATUS_DENY,
          ).label,
        },
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "project_code",
        title: "Code du projet (project_code)",
        render: (rowData) => (rowData.project ? rowData.project.code : ""),
        orderField: "projects.code",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "project_label",
        title: "Nom du projet (project_label)",
        render: (rowData) => (rowData.project ? rowData.project.label : ""),
        orderField: "projects.label",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "date",
        title: "Date (date)",
        render: (rowData) => (rowData.date ? moment(rowData.date).format(dateFormat) : ""),
        type: "string",
        hidden: false,
        filterPlaceholder: "De // À (JJ-MM-AAAA)",
        cellStyle: { minWidth: "130px" },
        defaultSort: "desc",
      },
      {
        field: "user_name",
        title: "Créé par (user_name)",
        type: "string",
        render: (rowData) =>
          rowData.user ? `${(rowData.user || {}).name} ${(rowData.user || {}).surname}` : "",
        orderField: "users.name",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "cost",
        title: "Montant ($) (cost)",
        type: "numeric",
        render: (rowData) => `${Number(rowData.cost) / 100}`,
        hidden: false,
        filtering: false,
        cellStyle: ellipsis,
      },
      {
        field: "description",
        title: "Description (description)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "solution",
        title: "Solution (solution)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
    ],
    onRowClick: (e, data) => history.push(`/notes/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter une note",
        isFreeAction: true,
        onClick: (event) => history.push("/notes/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer la note",
        onClick: (event, rowData) => history.push("/notes/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },
  sse: {
    title: "CLB - Intervention SSE",
    service: noteService,
    columns: [
      {
        field: "id",
        title: "# (id)",
        render: (rowData) => `# ${rowData.id}`,
        orderField: "notes.id",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "80px" },
      },
      {
        field: "intervention_type",
        title: "Type d'intervention (intervention_type)",
        render: (rowData) => {
          const t = interventionType(rowData.intervention_type);
          if (!t) return "";
          return <span className={`sse-type-tag sse-type-tag--${t.key}`}>{t.label}</span>;
        },
        exportTransformer: (column, row) =>
          (interventionType(row.intervention_type) || {}).label || "",
        type: "string",
        lookup: INTERVENTION_TYPES.reduce((all, t) => ({ ...all, [t.id]: t.label }), {}),
        hidden: false,
        cellStyle: { minWidth: "140px" },
      },
      {
        field: "project_code",
        title: "Code du projet (project_code)",
        render: (rowData) => (rowData.project ? rowData.project.code : ""),
        orderField: "projects.code",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "project_label",
        title: "Nom du projet (project_label)",
        render: (rowData) => (rowData.project ? rowData.project.label : ""),
        orderField: "projects.label",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "date",
        title: "Date (date)",
        render: (rowData) => (rowData.date ? moment(rowData.date).format(dateFormat) : ""),
        type: "string",
        hidden: false,
        filterPlaceholder: "De // À (JJ-MM-AAAA)",
        cellStyle: { minWidth: "130px" },
        defaultSort: "desc",
      },
      {
        field: "user_name",
        title: "Créée par (user_name)",
        type: "string",
        render: (rowData) =>
          rowData.user ? `${(rowData.user || {}).name} ${(rowData.user || {}).surname}` : "",
        orderField: "users.name",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "text",
        title: "Contenu (text)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
    ],
    onRowClick: (e, data) => history.push(`/sse/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter une intervention",
        isFreeAction: true,
        onClick: (event) => history.push("/sse/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer l'intervention",
        onClick: (event, rowData) => history.push("/sse/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },
  projects: {
    title: "CLB - Projets",
    service: projectService,
    processFilters: function (filters) {
      // retourne les filtres pour les requêtes API
      // console.log(filters)
      return filters; //.filter(f => f.column.field !== 'disabled')
    },
    processData: function (data, filters) {
      // retourne les données à afficher
      // let disabled = filters.find(f => f.column.field === 'disabled')
      // if (disabled){
      //     data = data.filter(p => p.disabled === (disabled.value !== "unchecked"))
      // }
      return data;
    },
    columns: [
      {
        field: "disabled",
        title: "Fermé (disabled)",
        type: "boolean",
        hidden: false,
        defaultSort: "asc",
      },
      {
        field: "code",
        title: "Code (code)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "200px" },
      },
      {
        field: "label",
        title: "Nom (label)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "user_name",
        title: "Responsables (user_name)",
        type: "string",
        render: (rowData) =>
          rowData.users ? rowData.users.map((u) => u.name + " " + u.surname).join(", ") : "",
        orderField: "users.name",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "type_id",
        title: "Type (type_id)",
        render: (rowData) => (rowData.project_type ? rowData.project_type.label : ""),
        orderField: "project_types.label",
        type: "string",
        lookup: {},
        lookupKey: "ProjectType",
        hidden: false,
        cellStyle: { minWidth: "150px" },
      },
      {
        field: "client_name",
        title: "Client (client_name)",
        render: (rowData) => (rowData.client ? rowData.client.name : ""),
        orderField: "clients.name",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "address",
        title: "Adresse (address)",
        type: "string",
        hidden: true,
        cellStyle: ellipsis,
      },
      {
        field: "date_begin",
        title: "Date de début (date_begin)",
        render: (rowData) =>
          rowData.date_begin ? moment(rowData.date_begin).format(dateFormat) : "",
        type: "date",
        filtering: false,
        hidden: true,
        cellStyle: { minWidth: "130px" },
      },
      {
        field: "date_end",
        title: "Date de fin (date_end)",
        render: (rowData) => (rowData.date_end ? moment(rowData.date_end).format(dateFormat) : ""),
        type: "date",
        filtering: false,
        hidden: true,
        cellStyle: { minWidth: "130px" },
      },
    ],
    onRowClick: (e, data) => history.push(`/projects/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un projet",
        isFreeAction: true,
        onClick: (event) => history.push("/projects/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer le projet",
        onClick: (event, rowData) => history.push("/projects/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },
  tailgates: {
    title: "CLB - Tailgates",
    service: tailgateService,
    columns: [
      {
        field: "published",
        title: "Publié (published)",
        type: "boolean",
        hidden: false,
      },
      {
        field: "code",
        title: "Code du tailgate (code)",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "200px" },
      },
      {
        field: "project_label",
        title: "Nom du projet (project_label)",
        render: (rowData) => (rowData.project ? rowData.project.label : ""),
        orderField: "projects.label",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "user_name",
        title: "Créé par (user_id)",
        render: (rowData) => (rowData.user ? rowData.user.name + " " + rowData.user.surname : ""),
        orderField: "users.name",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "date",
        title: "Date (date)",
        render: (rowData) => (rowData.date ? moment(rowData.date).format(dateFormat) : ""),
        type: "date",
        filtering: false,
        hidden: false,
        cellStyle: { minWidth: "130px" },
        defaultSort: "desc",
      },
    ],
    onRowClick: (e, data) => history.push(`/tailgates/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un tailgate",
        isFreeAction: true,
        onClick: (event) => history.push("/tailgates/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer le tailgate",
        onClick: (event, rowData) => history.push("/tailgates/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },
  users: {
    title: "CLB - Utilisateurs",
    service: userService,
    columns: [
      {
        field: "type_id",
        title: "Type (type_id)",
        render: (rowData) => (rowData.user_type ? rowData.user_type.label : ""),
        orderField: "user_types.label",
        type: "string",
        lookup: {},
        lookupKey: "UserType",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "job_id",
        title: "Travail (job_id)",
        render: (rowData) => (rowData.job ? rowData.job.label : ""),
        orderField: "jobs.label",
        type: "string",
        lookup: {},
        lookupKey: "Job",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "name",
        title: "Prénom (name)",
        type: "string",
        hidden: false,
      },
      {
        field: "surname",
        title: "Nom (surname)",
        type: "string",
        hidden: false,
      },
      {
        field: "email",
        title: "Courriel (email)",
        type: "string",
        hidden: false,
      },
    ],
    onRowClick: (e, data) => history.push(`/users/${data.id}`),
    actions: [
      {
        icon: "person_add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un utilisateur",
        isFreeAction: true,
        onClick: (event) => history.push("/users/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer l'utilisateur",
        onClick: (event, rowData) => history.push("/users/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },

  submissions: {
    title: "CLB - Soumissions",
    service: submissionService,
    columns: [
      {
        field: "num",
        title: "No de soumission (num)",
        type: "string",
        hidden: false,
        defaultSort: "desc",
        sorting: true,
      },
      {
        field: "title",
        title: "Titre (title)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "submission_state_id",
        title: "État (submission_state_id)",
        render: (rowData) => (rowData.submission_state ? rowData.submission_state.label : ""),
        orderField: "submission_states.label",
        type: "string",
        lookup: {},
        lookupKey: "SubmissionState",
        filterComponent: OrderedLookupFilter,
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "project_type_id",
        title: "Type de projet (project_type_id)",
        render: (rowData) => (rowData.project_type ? rowData.project_type.label : ""),
        orderField: "project_types.label",
        type: "string",
        lookup: {},
        lookupKey: "ProjectType",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "region_id",
        title: "Région (region_id)",
        render: (rowData) => (rowData.region ? rowData.region.label : ""),
        orderField: "regions.label",
        type: "string",
        lookup: {},
        lookupKey: "Region",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "user_name",
        title: "Estimateur (user_name)",
        render: (rowData) => (rowData.user ? rowData.user.name + " " + rowData.user.surname : ""),
        orderField: "users.name",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "client_name",
        title: "Client (client_name)",
        render: (rowData) => (rowData.client ? rowData.client.name : ""),
        orderField: "clients.name",
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
      },
      {
        field: "client_manager",
        title: "Responsable client (client_manager)",
        type: "string",
        hidden: false,
      },
      {
        field: "ammount",
        title: "Montant déposé (ammount)",
        type: "number",
        hidden: false,
      },
      {
        field: "date_visite",
        title: "Date de visite (date_visite)",
        render: (rowData) =>
          rowData.date_visite ? moment(rowData.date_visite).format(dateFormat) : "",
        type: "date",
        hidden: false,
        filtering: false,
        filterPlaceholder: "De // À (JJ-MM-AAAA)",
        cellStyle: { minWidth: "130px" },
      },
      {
        field: "date_depot",
        title: "Date de dépot (date_depot)",
        render: (rowData) =>
          rowData.date_depot ? moment(rowData.date_depot).format(dateFormat) : "",
        type: "date",
        hidden: false,
        filtering: false,
        filterPlaceholder: "De // À (JJ-MM-AAAA)",
        cellStyle: { minWidth: "130px" },
      },
      {
        field: "caution",
        title: "Caution / Assurance (caution)",
        type: "boolean",
        hidden: false,
      },
    ],
    onRowClick: (e, data) => history.push(`/submissions/${data.id}`),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un soumission",
        isFreeAction: true,
        onClick: (event) => history.push("/submissions/create"),
      },
      {
        icon: "edit",
        tooltip: "Éditer la soumission",
        onClick: (event, rowData) => history.push("/submissions/" + rowData.id + "/edit"),
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },

  documents: {
    title: "CLB - Médias",
    service: documentService,
    columns: [
      {
        field: "type_id",
        title: "Type (type_id)",
        render: (rowData) => (rowData.document_type ? rowData.document_type.label : ""),
        orderField: "document_types.label",
        type: "string",
        lookup: {},
        lookupKey: "DocumentType",
        hidden: false,
      },
      {
        field: "label",
        title: "Libellé (label)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
        defaultSort: "desc",
      },
      {
        field: "model",
        title: "Associé à (model)",
        render: (rowData) => {
          const model = rowData.model;
          if (model === "Project") {
            let project = (rowData.project_documents[0] || {}).project || {};
            return "[Projet] " + project.code + " : " + project.label;
          }
          if (model === "User") {
            let user = (rowData.user_documents[0] || {}).user || {};
            return "[Utilisateur] " + user.name + " " + user.surname;
          }
          if (model === "Tailgate") {
            let tailgate = (rowData.tailgate_documents[0] || {}).tailgate || {};
            return "[Tailgate] " + tailgate.code;
          }
          return model;
        },
        type: "string",
        lookup: {
          Project: "Projet",
          User: "Utilisateur",
          Tailgate: "Tailgate",
        },
        hidden: false,
      },
    ],
    editable: {
      onRowDelete: (oldData) =>
        new Promise((resolve, reject) => {
          documentService
            .remove(oldData)
            .then(() => resolve({ data: [] }))
            .catch(() => resolve({ data: [] }));
        }),
    },
    onRowClick: (e, data) => previewDocument(data),
    actions: [
      {
        icon: "add",
        iconProps: { className: "main" },
        tooltip: "Ajouter un média",
        isFreeAction: true,
        onClick: (event) => history.push("/documents/create"),
      },
      {
        icon: "cloud_download",
        tooltip: "Télécharger le fichier",
        onClick: (event, rowData) => {
          documentService.get(rowData.id).then((result) => {
            saveAs(result, rowData.filename);
          });
        },
      },
      {
        icon: "import_export",
        iconProps: { className: "main" },
        tooltip: "Power Bi",
        isFreeAction: true,
      },
    ],
  },

  suppliers: {
    title: "CLB - Fournisseurs",
    service: selectService,
    fetcher: "listSuppliers",
    columns: [
      {
        field: "name",
        title: "Nom (name)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "region_id",
        title: "Région (region_id)",
        render: (rowData) => (rowData.region ? rowData.region.label : ""),
        orderField: "regions.label",
        type: "string",
        lookup: {},
        lookupKey: "Region",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "zipcode",
        title: "Code postal (zipcode)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "address",
        title: "Addresse (address)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "city",
        title: "Ville (city)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "mail",
        title: "Courriel (mail)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "tel",
        title: "No téléphone (tel)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "note",
        title: "Notes (note)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "specialities",
        title: "Spécialité (specialities)",
        render: (rowData) => (
          <SelectsContext.Consumer>
            {(selects) =>
              React.Children.toArray(
                rowData.specialities
                  .map((s) => s.speciality_type_id)
                  .filter((stId, i, arr) => i === arr.indexOf(stId)) // élimine les doublons
                  .map((stId) => (
                    <Tag>{selects.SpecialityType.find((st) => st.id === stId).label}</Tag>
                  )),
              )
            }
          </SelectsContext.Consumer>
        ),
        exportTransformer: (column, row) =>
          row.specialities
            .map((s) => s.speciality_type_id)
            .filter((stId, i, arr) => i === arr.indexOf(stId)) // élimine les doublons
            .map((stId) => column.lookup[`${stId}`])
            .join(", "),
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
        readonly: true,
        editable: "never",
        lookup: {},
        lookupKey: "SpecialityType",
      },
      {
        field: "supplier_specialities",
        title: "Sous-spécialités (supplier_specialities)",
        render: (rowData) => {
          return (
            <>{React.Children.toArray(rowData.specialities.map((s) => <Tag>{s.label}</Tag>))}</>
          );
        },
        exportTransformer: (column, row) =>
          row.specialities.map((specialite) => specialite.label).join(", "),
        type: "string",
        hidden: false,
        cellStyle: { minWidth: "220px" },
        lookup: {},
        lookupKey: "Speciality",
        editComponent: (props) => {
          return (
            <SelectsContext.Consumer>
              {(selects) => (
                <Select
                  mode="multiple"
                  value={props.value != null ? props.value.map((o) => o.speciality_id) : []}
                  onChange={(values) => {
                    //console.log('COUCOU speciality selects:', values)
                    props.onChange(
                      values.map((val) => ({
                        speciality_id: val,
                      })),
                    );
                  }}
                  style={{ width: "100%" }}
                  {...selectProps()}
                >
                  {selects.Speciality &&
                    selects.Speciality.map((s, i) => (
                      <Select.Option key={i} value={s.id}>
                        {s.label}
                      </Select.Option>
                    ))}
                </Select>
              )}
            </SelectsContext.Consumer>
          );
        },
      },
      {
        field: "fav",
        title: "Favoris (fav)",
        type: "boolean",
        hidden: false,
        cellStyle: ellipsis,
      },
    ],
    editable: {
      onRowAdd: (newData) =>
        new Promise((resolve, reject) => {
          newData.supplier_specialities_attributes = newData.supplier_specialities;
          selectService
            .createSupplier(newData)
            .then(() => resolve({ data: [] }))
            .catch(() => resolve({ data: [] }));
        }).catch((err) => {
          console.error(err);
        }),
      onRowUpdate: (newData, oldData) =>
        new Promise((resolve, reject) => {
          // console.log('Promise to update')
          // ajoute une entrée pour chaque specialité nouvellement sélectionnée
          newData.supplier_specialities_attributes = newData.supplier_specialities
            .filter(
              (newS) =>
                !oldData.supplier_specialities.some(
                  (oldS) => newS.speciality_id === oldS.speciality_id,
                ),
            )
            .map((s) => ({ speciality_id: s.speciality_id }));
          //console.log('COUCOU supplier_specialities_attributes', newData.supplier_specialities_attributes)
          // ajoute une entrée pour la suppression de chaque spécialité désélectionnée
          newData.supplier_specialities_attributes =
            newData.supplier_specialities_attributes.concat(
              oldData.supplier_specialities
                .filter(
                  (oldS) =>
                    !newData.supplier_specialities.some(
                      (newS) => newS.speciality_id === oldS.speciality_id,
                    ),
                )
                .map((o) => ({ id: o.id, _destroy: true })),
            );
          selectService
            .updateSupplier(newData)
            .then(() => resolve({ data: [] }))
            .catch((err) => {
              console.error(err);
              resolve({ data: [] });
            });
        }).catch((err) => {
          console.error(err);
        }),
    },
  },

  means: {
    title: "CLB - Moyens",
    service: selectService,
    fetcher: "listMeans",
    columns: [
      {
        field: "disabled",
        title: "Désactivé (disabled)",
        type: "boolean",
        hidden: false,
      },
      {
        field: "risk_id",
        title: "Risque (risk_id)",
        render: (rowData) => (rowData.risk ? rowData.risk.label : ""),
        orderField: "risks.label",
        type: "string",
        lookup: {},
        lookupKey: "Risk",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "label",
        title: "Moyen (label)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "description",
        title: "Description (description)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
    ],
    editable: {
      onRowAdd: (newData) =>
        new Promise((resolve, reject) => {
          selectService
            .createMean(newData)
            .then(() => resolve({ data: [] }))
            .catch(() => resolve({ data: [] }));
        }),
      onRowUpdate: (newData, oldData) =>
        new Promise((resolve, reject) => {
          selectService
            .updateMean(newData)
            .then(() => resolve({ data: [] }))
            .catch(() => resolve({ data: [] }));
        }),
    },
  },

  risks: {
    title: "CLB - Risques",
    service: selectService,
    fetcher: "listRisks",
    columns: [
      {
        field: "disabled",
        title: "Désactivé (disabled)",
        type: "boolean",
        hidden: false,
      },
      {
        field: "label",
        title: "Risque (label)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
      {
        field: "description",
        title: "Description (description)",
        type: "string",
        hidden: false,
        cellStyle: ellipsis,
      },
    ],
    editable: {
      onRowAdd: (newData) =>
        new Promise((resolve, reject) => {
          selectService
            .createRisk(newData)
            .then(() => resolve({ data: [] }))
            .catch(() => resolve({ data: [] }));
        }),
      onRowUpdate: (newData, oldData) =>
        new Promise((resolve, reject) => {
          selectService
            .updateRisk(newData)
            .then(() => resolve({ data: [] }))
            .catch(() => resolve({ data: [] }));
        }),
    },
  },
};
